import {
    type NodeCfg,
    type WorkOrderNodeOutputs,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import { removeNodeSocket } from '../../sync-utils/remove-node-socket';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import type { UiNodeSpec } from './UiNodeSpec';
import { accessor } from 'src/store';
import type { UiFlowNodeControl } from '../../controls/UiFlowNodeControl';

const workOrderColumnLabels = {
    orderNumber: 'Name',
    status: 'Status',
    noneCount: 'None Count',
    totalCount: 'Total Count',
    passedCount: 'Passed Count',
    failedCount: 'Failed Count',
    startedCount: 'Started Count',
    scrappedCount: 'Scrapped Count',
    completedCount: 'Completed Count',
    targetQuantity: 'Target Quantity',
    incompleteCount: 'Incomplete Count',
    rawMaterialData: 'Raw Material Data',
    quarantinedCount: 'Quarantined Count',
} satisfies {
    [key in WorkOrderNodeOutputs]: string;
};

const workOrderColumnSelectionMap = Object.entries(workOrderColumnLabels).map(([ key, value ]) => ({
    label: value,
    value: key,
}));

export const workOrderUiNodeSpec: UiNodeSpec<'workOrder'> = {
    kindLabel: 'Work Order',
    nodeCategory: 'argonautApi',
    requiredSockets: [ 'workOrderId', 'watchWorkOrder' ],
    controls: getNodeCfg => {
        return {
            subcribedColumns: {
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.subcribedColumns,
                cfg: {
                    selectLabel: 'Add Output',
                    kind: 'selectWithCallback',
                    items: () => {
                        const nodeCfg = getNodeCfg();
                        const notUsedOutputs = Object.entries(nodeCfg.controlValues.subcribedColumns).filter(([ , value ]) => !value).map(([ key ]) => key);
                        return workOrderColumnSelectionMap.filter(item => notUsedOutputs.includes(item.value));
                    },
                    onSelect (value) {
                        const nodeCfg = getNodeCfg();
                        const typedValue = value as keyof typeof nodeCfg.controlValues.subcribedColumns;
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    subcribedColumns: {
                                        ...nodeCfg.controlValues.subcribedColumns,
                                        [typedValue]: true,
                                    },
                                },
                            },
                        });
                    },
                },
            },
        };
    },
    cfg: () => {
        const workOrderNodeCfg: NodeCfg<'workOrder'> = {
            disabled: false,
            id: getUID(),
            kind: 'workOrder',
            controlValues: {
                subcribedColumns: {
                    status: true,
                    orderNumber: false,
                    noneCount: false,
                    totalCount: false,
                    passedCount: false,
                    failedCount: false,
                    startedCount: false,
                    targetQuantity: true,
                    completedCount: true,
                    scrappedCount: false,
                    incompleteCount: true,
                    rawMaterialData: false,
                    quarantinedCount: false,
                },
            },
        };
        return workOrderNodeCfg;
    },
    inputs: () => ({
        watchWorkOrder: { socket: 'event', label: 'Watch Work Order' },
        workOrderId: { socket: 'string', label: 'Work Order ID' },
    }),
    outputs: nodeCfg => {
        const outputs: ReturnType<UiNodeSpec<'workOrder'>['outputs']> = {
            workOrderUpdate: { socket: 'event', label: 'Work Order Update' },
        };

        for (const [ key, value ] of Object.entries(nodeCfg.controlValues.subcribedColumns)) {
            const typedKey = key as keyof typeof workOrderColumnLabels;
            if (value) {
                const control: UiFlowNodeControl.Opts<string> = {
                    nodeCfg,
                    value: '',
                    cfg: {
                        rightAlign: true,
                        kind: 'deletableSocket',
                        label: workOrderColumnLabels[typedKey],
                        onDelete () {
                            removeNodeSocket<'workOrder'>({
                                direction: 'output',
                                removalMethod: {
                                    kind: 'uniqueIdentifier',
                                    value: typedKey,
                                },
                                newNodeCfg: {
                                    ...nodeCfg,
                                    controlValues: {
                                        ...nodeCfg.controlValues,
                                        subcribedColumns: {
                                            ...nodeCfg.controlValues.subcribedColumns,
                                            [typedKey]: false,
                                        },
                                    },
                                },
                            });
                        },
                    },
                };
                if (typedKey === 'status') {
                    outputs[typedKey] = { control, socket: 'string' };
                } else if (typedKey === 'rawMaterialData') {
                    outputs[typedKey] = { control, socket: 'workOrderMaterialData' };
                } else {
                    outputs[typedKey] = { control, socket: 'number' };
                }
            }
        }

        return outputs;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        const subscribedItems = Object.entries(node.controlValues.subcribedColumns).filter(([ , value ]) => value);

        if (!subscribedItems.length) {
            errors.subcribedColumns = 'At least one output is required';
        }

        return Object.keys(errors).length ? errors : true;
    },
    suppressNode: () => !accessor.testFeatureFlagRequirements([ 'unstableWorkOrders' ]),
};
